import React, { useState, useEffect, useCallback, useRef } from 'react';
import './index.scss';

const GameSnake = () => {
  const [snake, setSnake] = useState([[10, 10]]);
  const [food, setFood] = useState([5, 5]);
  const [direction, setDirection] = useState('RIGHT');
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const gameLoopRef = useRef(null);
  const speedRef = useRef(150);

  const BOARD_SIZE = 20;
  const INITIAL_SPEED = 150;

  // 添加事件监听器
  useEffect(() => {
    const handleGameControl = (event) => {
      const { action, gameType } = event.detail;
      if (gameType !== 'snake') return;
      
      switch (action) {
        case 'up':
          if (!gameOver && !gamePaused) setDirection('UP');
          break;
        case 'left':
          if (!gameOver && !gamePaused) setDirection('LEFT');
          break;
        case 'right':
          if (!gameOver && !gamePaused) setDirection('RIGHT');
          break;
        case 'down':
          if (!gameOver && !gamePaused) setDirection('DOWN');
          break;
        case 'pause':
          if (!gameOver) togglePause();
          break;
        case 'restart':
          restartGame();
          break;
        default:
          break;
      }
    };

    window.addEventListener('gameControl', handleGameControl);
    return () => window.removeEventListener('gameControl', handleGameControl);
  }, [gameOver, gamePaused]);

  // 生成随机食物位置
  const generateFood = useCallback(() => {
    let newFood;
    do {
      newFood = [
        Math.floor(Math.random() * BOARD_SIZE),
        Math.floor(Math.random() * BOARD_SIZE)
      ];
    } while (snake.some(segment => segment[0] === newFood[0] && segment[1] === newFood[1]));
    return newFood;
  }, [snake]);

  // 检查碰撞
  const checkCollision = useCallback((head) => {
    // 检查墙壁碰撞
    if (head[0] < 0 || head[0] >= BOARD_SIZE || head[1] < 0 || head[1] >= BOARD_SIZE) {
      return true;
    }
    
    // 检查自身碰撞
    return snake.some(segment => segment[0] === head[0] && segment[1] === head[1]);
  }, [snake]);

  // 移动蛇
  const moveSnake = useCallback(() => {
    if (gameOver || gamePaused) return;

    setSnake(prevSnake => {
      const newSnake = [...prevSnake];
      const head = [...newSnake[0]];

      // 根据方向移动蛇头
      switch (direction) {
        case 'UP':
          head[1] -= 1;
          break;
        case 'DOWN':
          head[1] += 1;
          break;
        case 'LEFT':
          head[0] -= 1;
          break;
        case 'RIGHT':
          head[0] += 1;
          break;
        default:
          break;
      }

      // 检查碰撞
      if (checkCollision(head)) {
        setGameOver(true);
        return prevSnake;
      }

      newSnake.unshift(head);

      // 检查是否吃到食物
      if (head[0] === food[0] && head[1] === food[1]) {
        setScore(prevScore => {
          const newScore = prevScore + 10;
          if (newScore > bestScore) {
            setBestScore(newScore);
            localStorage.setItem('gameSnake_best_score', newScore.toString());
          }
          return newScore;
        });
        setFood(generateFood());
        // 增加速度
        speedRef.current = Math.max(50, INITIAL_SPEED - Math.floor(score / 50) * 10);
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [direction, food, gameOver, gamePaused, checkCollision, generateFood, score, bestScore]);

  // 游戏循环
  useEffect(() => {
    if (gameStarted && !gameOver && !gamePaused) {
      gameLoopRef.current = setInterval(moveSnake, speedRef.current);
    }
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, gamePaused, moveSnake]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!gameStarted) {
        if (e.key === ' ' || e.key === 'Enter') {
          startGame();
        }
        return;
      }

      if (e.key === ' ') {
        e.preventDefault();
        togglePause();
        return;
      }

      if (gamePaused) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (direction !== 'DOWN') setDirection('UP');
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (direction !== 'UP') setDirection('DOWN');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (direction !== 'RIGHT') setDirection('LEFT');
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (direction !== 'LEFT') setDirection('RIGHT');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction, gameStarted, gamePaused]);

  // 开始游戏
  const startGame = () => {
    setSnake([[10, 10]]);
    setFood(generateFood());
    setDirection('RIGHT');
    setGameOver(false);
    setScore(0);
    setGameStarted(true);
    setGamePaused(false);
    speedRef.current = INITIAL_SPEED;
  };

  // 重新开始游戏
  const restartGame = () => {
    startGame();
  };

  // 暂停/继续游戏
  const togglePause = () => {
    setGamePaused(prev => !prev);
  };

  // 初始化最高分
  useEffect(() => {
    const savedBestScore = localStorage.getItem('gameSnake_best_score');
    if (savedBestScore) {
      setBestScore(parseInt(savedBestScore));
    }
  }, []);

  // 渲染游戏板
  const renderBoard = () => {
    const board = [];
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const isSnake = snake.some(segment => segment[0] === x && segment[1] === y);
        const isHead = snake[0] && snake[0][0] === x && snake[0][1] === y;
        const isFood = food[0] === x && food[1] === y;
        
        let className = 'cell';
        if (isSnake) {
          className += isHead ? ' snake-head' : ' snake-body';
        } else if (isFood) {
          className += ' food';
        }

        board.push(
          <div
            key={`${x}-${y}`}
            className={className}
            style={{
              left: `${x * 20}px`,
              top: `${y * 20}px`
            }}
          />
        );
      }
    }
    return board;
  };

  return (
    <div className="game-snake">
      <div className="game-header">
        <div className="game-scores">
          <div className="score-container">
            <div className="score-label">分数</div>
            <div className="score-value">{score}</div>
          </div>
          <div className="score-container">
            <div className="score-label">最高分</div>
            <div className="score-value">{bestScore}</div>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="controls-row">
            {!gameStarted ? (
              <button className="start-btn" onClick={startGame}>
                开始游戏
              </button>
            ) : (
              <div className="control-buttons">
                <button className="control-btn" onClick={togglePause}>
                  {gamePaused ? '继续' : '暂停'}
                </button>
                <button className="control-btn" onClick={restartGame}>
                  重新开始
                </button>
              </div>
            )}
            
            <div className="mobile-controls">
              <button 
                className="control-btn up" 
                onClick={() => !gamePaused && direction !== 'DOWN' && setDirection('UP')}
              >
                ↑
              </button>
              <div className="horizontal-controls">
                <button 
                  className="control-btn left" 
                  onClick={() => !gamePaused && direction !== 'RIGHT' && setDirection('LEFT')}
                >
                  ←
                </button>
                <button 
                  className="control-btn down" 
                  onClick={() => !gamePaused && direction !== 'UP' && setDirection('DOWN')}
                >
                  ↓
                </button>
                <button 
                  className="control-btn right" 
                  onClick={() => !gamePaused && direction !== 'LEFT' && setDirection('RIGHT')}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="game-board-container">
        <div className="game-board">
          {renderBoard()}
        </div>
      </div>

      {gameOver && (
        <div className="game-overlay game-over">
          <div className="overlay-content">
            <h2>游戏结束</h2>
            <p>最终分数: {score}</p>
            <button onClick={restartGame}>重新开始</button>
          </div>
        </div>
      )}

      {gamePaused && !gameOver && (
        <div className="game-overlay paused">
          <div className="overlay-content">
            <h2>游戏暂停</h2>
            <p>按空格键继续游戏</p>
            <button onClick={togglePause}>继续</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameSnake; 